import React from 'react';
import { Stack, Typography, Button, List, ListItem, ListItemButton } from '@mui/material';
import { Add } from '@mui/icons-material';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { Collection } from '@domain/product/models/Collection';
import { useGenericDrawer } from '@shared/shell/drawer/useGenericDrawer';
import { useBottomSheet, BottomSheetSize } from '@shared/shell/bottomsheet/useBottomSheet';
import CollectionDetails from '@features/collections/components/CollectionDetails';
import { Product } from '@domain/product/models/Product';
import { LinkProductToCollectionsSheet } from '@features/collections';
import ProductSelectBottomSheet from '../selection/ProductSelectBottomSheet';
import CreateCollectionBottomSheet from '@features/collections/components/CreateCollectionBottomSheet';

interface ProductCollectionsProps {
  collections: Collection[] | undefined;
  product: Product;
}

const ProductCollections: React.FC<ProductCollectionsProps> = ({
  collections,
  product,
}) => {
  const { openDrawer } = useGenericDrawer();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();
  const handleCollectionClick = (collection: Collection) => {
    openDrawer(
      'Collection Details',
      <CollectionDetails collectionId={collection.id} />
    );
  };


  const handleAddToCollection = () => {
    if (!product) return;
    openBottomSheet({
      title: 'Add to collection',
      component: (
        <LinkProductToCollectionsSheet
          productId={product.id}
          existingCollectionIds={new Set(product.collections?.map(c => c.id) ?? [])}
          onClose={closeBottomSheet}
          onCreateNewCollection={() => {
            openBottomSheet({
              title: 'Select products',
              component: (
                <ProductSelectBottomSheet
                  initialSelections={[{ product, quantity: 1 }]}
                  confirmButtonText={count =>
                    `Continue with ${count} product${count !== 1 ? 's' : ''}`
                  }
                  onFinish={selections => {
                    closeBottomSheet();
                    openBottomSheet({
                      title: 'Create collection',
                      component: (
                        <CreateCollectionBottomSheet
                          initialProducts={selections.map(s => s.product)}
                          onClose={closeBottomSheet}
                        />
                      ),
                      size: BottomSheetSize.MD,
                    });
                  }}
                  onClose={closeBottomSheet}
                />
              ),
              size: BottomSheetSize.MD,
            });
          }}
        />
      ),
      size: BottomSheetSize.MD,
    });
  };


  return (
    <BorderedBlock
      sx={{ p: 0 }}
      header={
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="subtitle2">Collections</Typography>
          <Button endIcon={<Add />} variant="text" color="primary" onClick={handleAddToCollection}>
            Add to collection
          </Button>
        </Stack>
      }
    >
      <List disablePadding>
        {(!collections || collections.length === 0) && (
          <ListItem sx={{ p: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              No collections yet. Use Add to collection to link this product or create one.
            </Typography>
          </ListItem>
        )}
        {collections?.map((collection: Collection) => (
          <ListItemButton onClick={() => handleCollectionClick(collection)}
            key={collection.id}

          >
            <Stack direction="row" alignItems="center" flexGrow={1} spacing={2} p={2}>
              <Typography variant="body2">{collection.name}</Typography>
            </Stack>
          </ListItemButton>
        ))}
      </List>
    </BorderedBlock>
  );
};

export default ProductCollections;
